// ===========================================
// Stage Progress Bar Component
// ===========================================
// Segmented progress bar using admin-defined stage colors
// - Each segment represents one skill/stage
// - Opacity reflects status: COMPLETED=100%, IN_PROGRESS=60%, NOT_STARTED=25%
// - Optional click handler for teacher interaction

import { Box, Tooltip } from '@mui/material';
import type { SkillStatus } from '../../types/gamification.types';

// ===========================================
// TYPES
// ===========================================

export interface StageSegment {
  id: string;
  name: string;
  color: string | null;
  status: SkillStatus;
  skillProgressId?: string;
}

interface StageProgressBarProps {
  stages: StageSegment[];
  size?: 'small' | 'medium';
  onStageClick?: (stage: StageSegment) => void;
  showTooltip?: boolean;
  /** Optional avatar image URL to display at the current in-progress stage */
  avatarSrc?: string;
}

// ===========================================
// CONSTANTS
// ===========================================

const DEFAULT_STAGE_COLOR = '#4580E4';

const STATUS_OPACITY: Record<SkillStatus, number> = {
  COMPLETED: 1.0,
  IN_PROGRESS: 0.6,
  NOT_STARTED: 0.25,
};

const STATUS_LABELS: Record<SkillStatus, string> = {
  COMPLETED: 'Completed',
  IN_PROGRESS: 'In Progress',
  NOT_STARTED: 'Not Started',
};

const SIZE_CONFIG = {
  small: { height: 6, borderRadius: 3 },
  medium: { height: 12, borderRadius: 6 },
};

// ===========================================
// COMPONENT
// ===========================================

export default function StageProgressBar({
  stages,
  size = 'small',
  onStageClick,
  showTooltip = true,
  avatarSrc,
}: StageProgressBarProps) {
  const config = SIZE_CONFIG[size];
  const isClickable = !!onStageClick;

  if (stages.length === 0) return null;

  // Find the index of the current in-progress stage (or last completed if all done)
  const allCompleted = stages.every((s) => s.status === 'COMPLETED');
  const avatarStageIndex = allCompleted
    ? stages.length - 1
    : stages.findIndex((s) => s.status === 'IN_PROGRESS');
  const avatarSize = size === 'small' ? 28 : 36;

  return (
    <Box
      sx={{
        display: 'flex',
        gap: '2px',
        width: '100%',
        height: config.height,
        position: 'relative',
      }}
    >
      {stages.map((stage, index) => {
        const color = stage.color || DEFAULT_STAGE_COLOR;
        const opacity = STATUS_OPACITY[stage.status];
        const isFirst = index === 0;
        const isLast = index === stages.length - 1;

        const segment = (
          <Box
            key={stage.id}
            onClick={isClickable ? () => onStageClick(stage) : undefined}
            role={isClickable ? 'button' : undefined}
            aria-label={`${stage.name} - ${STATUS_LABELS[stage.status]}`}
            tabIndex={isClickable ? 0 : undefined}
            onKeyDown={
              isClickable
                ? (e: React.KeyboardEvent) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      onStageClick(stage);
                    }
                  }
                : undefined
            }
            sx={{
              flex: 1,
              height: '100%',
              backgroundColor: color,
              opacity,
              borderRadius: `${isFirst ? config.borderRadius : 0}px ${isLast ? config.borderRadius : 0}px ${isLast ? config.borderRadius : 0}px ${isFirst ? config.borderRadius : 0}px`,
              cursor: isClickable ? 'pointer' : 'default',
              transition: 'opacity 0.2s ease',
              ...(isClickable && {
                '&:hover': {
                  opacity: Math.min(opacity + 0.2, 1.0),
                },
              }),
            }}
          />
        );

        if (showTooltip) {
          return (
            <Tooltip
              key={stage.id}
              title={`${stage.name} - ${STATUS_LABELS[stage.status]}`}
              arrow
              placement="top"
            >
              {segment}
            </Tooltip>
          );
        }

        return segment;
      })}
      {avatarSrc && avatarStageIndex >= 0 && (
        <Box
          component="img"
          src={avatarSrc}
          alt=""
          sx={{
            position: 'absolute',
            width: avatarSize,
            height: avatarSize,
            borderRadius: '50%',
            border: '2px solid white',
            boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
            top: '50%',
            transform: 'translate(-50%, -50%)',
            // Position at the center of the in-progress segment
            left: `${((avatarStageIndex + 0.5) / stages.length) * 100}%`,
            bgcolor: 'white',
            objectFit: 'contain',
            zIndex: 1,
            pointerEvents: 'none',
          }}
        />
      )}
    </Box>
  );
}
